import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { codexApi, downloadBlob } from "../lib/api";
import { ensureSession } from "../lib/storage";
import { useSilentMic, useSpeechSynthesis } from "../lib/voice";
import SourceTrace from "../components/SourceTrace";
import CompareWitnesses from "../components/CompareWitnesses";
import HeroEmblem, { FullBleedHero } from "../components/HeroEmblem";
import EulaModal from "../components/EulaModal";
import CallModal from "../components/CallModal";
import DisclaimerGate from "../components/DisclaimerGate";
import DonationCard from "../components/DonationCard";
import InstallAppButton from "../components/InstallAppButton";
import {
    Send,
    FileDown,
    Loader2,
    AlertCircle,
    Mic,
    MicOff,
    Volume2,
    VolumeX,
    Phone,
    Square,
    MessageSquareText,
    FilePlus2,
    BookOpen,
    Mail,
    FileText,
    ArrowLeft,
    Maximize2,
    X,
} from "lucide-react";
import { toast } from "sonner";

export default function SinglePage() {
    const [tab, setTab] = useState("chat");
    const [eulaOpen, setEulaOpen] = useState(false);

    return (
        <div className="space-y-10" data-testid="single-page">
            {/* Hero — transparent panel, dropped down the page so it sits near
                the scroll/parchment area in the scrolling background artwork. */}
            <section
                className="animate-rise flex justify-center px-4"
                style={{ paddingTop: "clamp(14rem, 28vh, 22rem)" }}
            >
                <div
                    className="codex-card max-w-3xl w-full px-6 sm:px-10 py-8 sm:py-10 text-center"
                    data-testid="hero-card"
                >
                    <h1
                        className="font-serif-display text-[#F9E596] tracking-tight leading-[1.02] drop-shadow-[0_3px_14px_rgba(0,0,0,0.85)]"
                        style={{ fontSize: "clamp(2rem, 5vw, 4rem)" }}
                    >
                        Ask the Codex OS<br />about any topic of reality.
                    </h1>
                    <p className="mt-5 text-[#F1F5F9] text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                        Route any question through the Codex's interpretive lens. Synthesize a downloadable study.
                    </p>
                </div>
            </section>

            {/* Install-the-app prompt — sits between hero and chat. The
                component self-hides when the app is already installed,
                running standalone, or the browser doesn't expose a PWA
                install path. iOS Safari opens an instructions modal
                instead since it can't expose programmatic install. */}
            <InstallAppButton />
            <DisclaimerGate />

            {/* Tab toggle lives inside the surface header now */}
            {tab === "chat" ? (
                <ChatSurface tab={tab} setTab={setTab} />
            ) : (
                <StudySurface tab={tab} setTab={setTab} />
            )}

            {/* Companion buttons — Library on top, Restoration Matrix below. Stacked vertically so each is its own plaque. */}
            <section
                className="px-4 flex flex-col items-center gap-5"
                data-testid="companion-buttons-section"
            >
                <Link
                    to="/library"
                    data-testid="library-cta"
                    className="restoration-cta restoration-cta--library group"
                >
                    <span className="restoration-cta-glyph" aria-hidden="true">
                        <svg viewBox="0 0 32 32" width="22" height="22" fill="none">
                            <path
                                d="M6 5h7a3 3 0 0 1 3 3v19a3 3 0 0 0-3-3H6V5Z"
                                stroke="currentColor"
                                strokeWidth="1"
                                fill="none"
                            />
                            <path
                                d="M26 5h-7a3 3 0 0 0-3 3v19a3 3 0 0 1 3-3h7V5Z"
                                stroke="currentColor"
                                strokeWidth="1"
                                fill="none"
                            />
                            <circle cx="16" cy="6" r="1.4" fill="currentColor" />
                        </svg>
                    </span>
                    <span className="restoration-cta-text">
                        <span className="restoration-cta-eyebrow">Browse the sealed shelf</span>
                        <span className="restoration-cta-title">The Codex Library</span>
                        <span className="restoration-cta-sub">Curated entries · source-traced</span>
                    </span>
                    <span className="restoration-cta-arrow" aria-hidden="true">→</span>
                </Link>

                <a
                    href="https://restorationmatrix.org"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="restoration-matrix-cta"
                    className="restoration-cta group"
                >
                    <span className="restoration-cta-glyph" aria-hidden="true">
                        <svg viewBox="0 0 32 32" width="22" height="22" fill="none">
                            <circle cx="16" cy="16" r="14" stroke="currentColor" strokeWidth="0.8" opacity="0.55" />
                            <polygon
                                points="16,2 19,13 30,16 19,19 16,30 13,19 2,16 13,13"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="0.9"
                            />
                            <circle cx="16" cy="16" r="1.6" fill="currentColor" />
                        </svg>
                    </span>
                    <span className="restoration-cta-text">
                        <span className="restoration-cta-eyebrow">Enter the companion volume</span>
                        <span className="restoration-cta-title">The Restoration Matrix</span>
                        <span className="restoration-cta-sub">RestorationMatrix.org</span>
                    </span>
                    <span className="restoration-cta-arrow" aria-hidden="true">→</span>
                </a>
            </section>

            <div className="gold-divider max-w-7xl mx-auto px-4 sm:px-8 lg:px-14" />

            <DonationCard />

            <footer className="px-4 pb-12">
                <div
                    className="codex-card max-w-3xl mx-auto px-6 py-6 text-center space-y-3"
                    data-testid="footer-card"
                >
                    <p className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#CBD5E1]">
                        © 2026 The New Jerusalem Codex Press · NewJerusalemCodex.org
                    </p>
                    <p className="text-[11px] text-[#CBD5E1] max-w-xl mx-auto leading-relaxed">
                        THE NEW JERUSALEM CODEX™ and THE RESTORATION MATRIX™ are claimed trademarks of
                        the owner/licensor. The governing Codex OS file is sealed.
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3 pt-2">
                        <a
                            href="mailto:ServantoftheBride@gmail.com?subject=The%20New%20Jerusalem%20Codex%20%E2%80%94%20Contact&body=%0A%0A%E2%80%94%20Sent%20from%20newjerusalemcodex.org"
                            data-testid="footer-contact-link"
                            className="codex-button codex-button-primary !px-6 !py-2.5 !text-[11px]"
                        >
                            <Mail size={14} strokeWidth={1.6} />
                            Contact Us
                        </a>
                        <button
                            type="button"
                            onClick={() => setEulaOpen(true)}
                            data-testid="footer-eula-link"
                            className="codex-button !px-5 !py-2.5 !text-[11px]"
                        >
                            <FileText size={14} strokeWidth={1.6} />
                            End-User License Agreement
                        </button>
                    </div>
                    <p
                        className="font-serif-display italic text-[12px] text-[#F9E596]/80 tracking-wide pt-2"
                        data-testid="footer-signoff"
                    >
                        — MidnightCry
                    </p>
                </div>
            </footer>
            <EulaModal open={eulaOpen} onClose={() => setEulaOpen(false)} />
        </div>
    );
}

function TabButton({ active, onClick, testid, children }) {
    return (
        <button
            data-testid={testid}
            onClick={onClick}
            className={`relative px-4 py-2 text-[10px] tracking-[0.28em] uppercase font-serif-display italic transition-all duration-300 flex items-center gap-2 rounded-full ${
                active
                    ? "text-[#FFF6D4]"
                    : "text-[#94A3B8] hover:text-[#F3D78E]"
            }`}
            style={
                active
                    ? {
                          background:
                              "linear-gradient(180deg, rgba(249,229,150,0.28), rgba(212,175,55,0.16))",
                          boxShadow:
                              "inset 0 1px 0 rgba(255,246,212,0.35), 0 0 18px -4px rgba(212,175,55,0.5)",
                      }
                    : undefined
            }
        >
            {children}
        </button>
    );
}

function SurfaceTabs({ tab, setTab }) {
    return (
        <div
            className="inline-flex items-center gap-1 p-1 rounded-full border border-[#D4AF37]/40 bg-[#070D1A]/20"
            data-testid="surface-toggle"
        >
            <TabButton active={tab === "chat"} onClick={() => setTab("chat")} testid="tab-chat">
                <MessageSquareText size={12} strokeWidth={1.6} />
                <span className="hidden sm:inline">Chat with the OS</span>
                <span className="sm:hidden">Chat</span>
            </TabButton>
            <TabButton active={tab === "study"} onClick={() => setTab("study")} testid="tab-study">
                <FilePlus2 size={12} strokeWidth={1.6} />
                <span className="hidden sm:inline">Generate PDF Study</span>
                <span className="sm:hidden">Study</span>
            </TabButton>
        </div>
    );
}

function DepthToggle() {
    return null; // removed — Deep mode is now the only mode
}

// --------------------------------------------------------------------------
// Chat surface
// --------------------------------------------------------------------------

function ChatSurface({ tab, setTab }) {
    const [messages, setMessages] = useState([]);
    const [input, setInput] = useState("");
    const [loading, setLoading] = useState(false);
    const [autoSpeak, setAutoSpeak] = useState(false);
    const [callOpen, setCallOpen] = useState(false);
    const [studyBusy, setStudyBusy] = useState(false);
    // Focused/expanded chat mode — fills the viewport so the user can think
    // and read without page chrome around them. Triggered on input focus or
    // first keystroke; exited via the "Back" button or the Escape key.
    const [focused, setFocused] = useState(false);
    // Visible viewport height — on mobile this shrinks when the on-screen
    // keyboard appears, which we use to size the focused panel so the
    // input field never gets hidden behind the keyboard.
    const [vvHeight, setVvHeight] = useState(
        typeof window !== "undefined"
            ? (window.visualViewport && window.visualViewport.height) || window.innerHeight
            : 0
    );
    // Every chat now routes through Deep mode (all Codex engines, with each
    // engine self-filtering NOT_APPLICABLE). The Fast/Deep toggle has been
    // removed per product direction — Deep is always on.
    const depth = "deep";
    const audioRef = useRef(null);
    const askAbortRef = useRef(null);
    const [audioPlaying, setAudioPlaying] = useState(false);
    const [activeMsgTs, setActiveMsgTs] = useState(null);
    const [lastQuery, setLastQuery] = useState("");
    const [interruptedAt, setInterruptedAt] = useState(null);
    const mode = "chat";
    const sessionId = useRef(ensureSession()).current;
    const scrollRef = useRef(null);
    const inputRef = useRef(input);
    inputRef.current = input;

    const sendDebounceRef = useRef(null);
    const justSubmittedVoiceRef = useRef(false);
    // Silent voice flow: MediaRecorder + Whisper → no browser chime/beep,
    // just a gold pulse-glow on the mic button while listening. When the
    // transcript comes back from Whisper we drop it straight into the input
    // and submit on the same turn.
    const handleVoiceResult = (text, isFinal) => {
        if (justSubmittedVoiceRef.current || !isFinal || !text) return;
        justSubmittedVoiceRef.current = true;
        setInput(text);
        if (sendDebounceRef.current) {
            clearTimeout(sendDebounceRef.current);
            sendDebounceRef.current = null;
        }
        handleSend(text);
        setTimeout(() => {
            justSubmittedVoiceRef.current = false;
        }, 400);
    };

    const {
        supported: micSupported,
        listening,
        busy: micBusy,
        start: startMic,
        stop: stopMic,
        abort: abortMic,
    } = useSilentMic({
        onResult: handleVoiceResult,
        silenceMs: 2200,
        transcribe: (blob, name) => codexApi.transcribe(blob, name),
    });
    const { supported: ttsSupported, speaking, speak, stop: stopSpeak } = useSpeechSynthesis();

    useEffect(() => {
        // While the OS is composing (loading=true) keep the latest user
        // message + spinner in view at the bottom. Once the OS reply lands,
        // scroll the TOP of that newest OS message into view so the reader
        // starts at the beginning of the answer — not pinned to the end.
        if (loading) {
            scrollRef.current?.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: "smooth",
            });
            return;
        }
        const last = messages[messages.length - 1];
        if (!last || last.role !== "os") return;
        const el = document.querySelector(`[data-os-ts="${last.ts}"]`);
        if (el) {
            el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    }, [messages, loading]);

    // Focused-mode escape hatch (keyboard) + body-scroll lock that
    // actually works on iOS / Android (overflow:hidden alone is ignored
    // by mobile touch-scroll; we have to pin the body's position too).
    useEffect(() => {
        if (!focused) return;
        const scrollY = window.scrollY;
        const body = document.body;
        const prev = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
            overscroll: document.documentElement.style.overscrollBehavior,
        };
        body.style.overflow = "hidden";
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.width = "100%";
        document.documentElement.style.overscrollBehavior = "contain";

        // Track the visible viewport so the focused panel always sizes to
        // whatever sits ABOVE the mobile keyboard.
        const vv = window.visualViewport;
        const sync = () => {
            const h = vv ? vv.height : window.innerHeight;
            setVvHeight(h);
        };
        sync();
        if (vv) {
            vv.addEventListener("resize", sync);
            vv.addEventListener("scroll", sync);
        } else {
            window.addEventListener("resize", sync);
        }

        const onKey = (e) => {
            if (e.key === "Escape") setFocused(false);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            body.style.overflow = prev.overflow;
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.width = prev.width;
            document.documentElement.style.overscrollBehavior = prev.overscroll;
            window.scrollTo(0, scrollY);
            window.removeEventListener("keydown", onKey);
            if (vv) {
                vv.removeEventListener("resize", sync);
                vv.removeEventListener("scroll", sync);
            } else {
                window.removeEventListener("resize", sync);
            }
        };
    }, [focused]);

    async function handleSend(q) {
        const query = (q ?? inputRef.current).trim();
        if (!query || loading) return;
        if (sendDebounceRef.current) {
            clearTimeout(sendDebounceRef.current);
            sendDebounceRef.current = null;
        }
        if (listening) stopMic();
        setInput("");
        setLastQuery(query);
        setInterruptedAt(null);
        setMessages((m) => [...m, { role: "user", text: query, ts: Date.now() }]);
        setLoading(true);
        try {
            const res = await codexApi.ask(query, sessionId, mode, false, depth);
            const osMessage = {
                role: "os",
                text: res.answer,
                layers: res.layers_consulted || 0,
                blocked: res.blocked,
                mode: res.mode,
                depth: res.depth || depth,
                engines: res.engines_used || [],
                followUps: res.follow_ups || [],
                query,
                ts: Date.now(),
            };
            setMessages((m) => [...m, osMessage]);
            if (autoSpeak && !res.blocked) {
                await playOnyx(res.answer);
            }
        } catch (e) {
            const detail = e?.response?.data?.detail || e.message || "Unknown error";
            setMessages((m) => [
                ...m,
                {
                    role: "os",
                    text: `RUNTIME_STATUS: ROUTING_FAILURE\nThe Codex OS could not resolve the route. ${detail}`,
                    layers: 0,
                    blocked: true,
                    ts: Date.now(),
                },
            ]);
        } finally {
            setLoading(false);
        }
    }

    async function handleDownloadFullStudy(query) {
        if (!query) return;
        const tId = toast.loading("Composing study via the Codex OS…");
        try {
            const packet = await codexApi.study(query, sessionId);
            const blob = await codexApi.studyPdf(packet);
            const safe = (packet.title || "codex_study")
                .replace(/[^a-z0-9_-]+/gi, "_")
                .toLowerCase();
            downloadBlob(blob, `${safe}.pdf`);
            toast.success("Study PDF downloaded.", { id: tId });
        } catch {
            toast.error("Could not generate study PDF.");
        }
    }

    /**
     * Synthesize a study packet from the running conversation. We hand the OS
     * the most recent user question as the topic seed, but the prompt's
     * CONVERSATION_HISTORY block (loaded server-side) already lets the OS draw
     * from every prior turn — so the resulting study reflects the whole
     * exchange, not just the last line.
     */
    async function handleGenerateStudyFromConversation() {
        const lastUser = [...messages].reverse().find((m) => m.role === "user");
        const topic = lastUser?.text?.trim();
        if (!topic) {
            toast.error("Ask a question first, then I can synthesize the study.");
            return;
        }
        setStudyBusy(true);
        const tId = toast.loading("Composing a study from this conversation…");
        try {
            const packet = await codexApi.study(topic, sessionId);
            const blob = await codexApi.studyPdf(packet);
            const safe = (packet.title || "codex_study")
                .replace(/[^a-z0-9_-]+/gi, "_")
                .toLowerCase();
            downloadBlob(blob, `${safe}.pdf`);
            toast.success("Study PDF downloaded.", { id: tId });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not generate study PDF.", { id: tId });
        } finally {
            setStudyBusy(false);
        }
    }

    function toggleMic() {
        if (!micSupported) {
            toast.error("Voice input isn't supported in this browser. Try Chrome or Safari.");
            return;
        }
        if (listening) stopMic();
        else startMic();
    }

    function toggleAutoSpeak() {
        setAutoSpeak((v) => !v);
        cleanupAudioRef();
    }

    function speakMessage(m) {
        // Onyx voice via OpenAI TTS, replacing the browser's default voice
        cleanupAudioRef();
        playOnyx(m.text);
    }

    function cleanupAudioRef() {
        if (audioRef.current) {
            try {
                if (typeof audioRef.current.cancel === "function") {
                    audioRef.current.cancel();
                }
                if (typeof audioRef.current.pause === "function") {
                    audioRef.current.pause();
                }
            } catch (_e) {
                // ignore
            }
            audioRef.current = null;
        }
    }

    async function playOnyx(text, msgTs) {
        try {
            cleanupAudioRef();
            if (msgTs) setActiveMsgTs(msgTs);

            // Rolling-pipeline playback. The answer is split into small
            // sentence-aligned chunks. We fire chunk 0's TTS immediately,
            // and the moment chunk 0's audio starts playing we fire chunk 1.
            // Only ever one TTS request is in flight (so OpenAI doesn't
            // throttle us), but the next chunk's audio is already loading
            // while the current chunk is being heard — so by the time
            // chunk N ends, chunk N+1 is ready, and the transition is
            // seamless. Time-to-first-audio is ~2-3 seconds.
            const full = (text || "").slice(0, 3800);
            const chunks = splitIntoChunks(full);
            if (!chunks.length) return;

            let cancelled = false;
            // Hold the resolver for whichever audio is currently playing so
            // an external interrupt can unwind the pipeline immediately
            // (audio.pause() does NOT fire onended — without this hook the
            // awaiting Promise would hang and handleSend's `loading=false`
            // in finally would never run, freezing the input).
            const cancelState = { resolveCurrent: null };
            const cancelSentinel = {
                cancel: () => {
                    cancelled = true;
                    if (cancelState.resolveCurrent) {
                        try { cancelState.resolveCurrent(); } catch (_e) { /* ignore */ }
                        cancelState.resolveCurrent = null;
                    }
                },
            };
            audioRef.current = cancelSentinel;
            setAudioPlaying(true);

            // Fire chunk 0 immediately.
            let currentPromise = codexApi.tts(chunks[0], "onyx");

            for (let i = 0; i < chunks.length; i++) {
                if (cancelled) return;
                let blob;
                try {
                    blob = await currentPromise;
                } catch (_e) {
                    currentPromise = i + 1 < chunks.length ? codexApi.tts(chunks[i + 1], "onyx") : null;
                    continue;
                }
                if (cancelled) return;

                // Kick off the next chunk's TTS NOW so it overlaps with
                // the current chunk's playback.
                currentPromise = i + 1 < chunks.length ? codexApi.tts(chunks[i + 1], "onyx") : null;

                const url = URL.createObjectURL(blob);
                const audio = new Audio(url);
                audioRef.current = audio;
                await new Promise((resolve) => {
                    cancelState.resolveCurrent = () => {
                        URL.revokeObjectURL(url);
                        try { audio.pause(); } catch (_e) { /* ignore */ }
                        resolve();
                    };
                    audio.onended = () => {
                        URL.revokeObjectURL(url);
                        cancelState.resolveCurrent = null;
                        resolve();
                    };
                    audio.onerror = () => {
                        URL.revokeObjectURL(url);
                        cancelState.resolveCurrent = null;
                        resolve();
                    };
                    audio.play().catch(() => {
                        cancelState.resolveCurrent = null;
                        resolve();
                    });
                });
                if (cancelled) return;
            }
            setAudioPlaying(false);
        } catch (e) {
            console.error("Onyx playback failed:", e);
            toast.error("Could not play the OS voice.");
            setAudioPlaying(false);
        }
    }

    // Split the answer into small sentence-aligned chunks so each TTS
    // request returns quickly. First chunk is intentionally TINY (~120
    // chars) so the OS starts speaking in ~2 seconds; subsequent chunks
    // grow to ~220 chars so we make fewer total requests for long answers.
    function splitIntoChunks(t) {
        if (!t) return [];
        if (t.length <= 260) return [t];
        const out = [];
        let i = 0;
        // First chunk: small for fast time-to-first-audio.
        // Following chunks: larger for fewer total requests.
        const cutAt = (txt, from, lo, hi) => {
            const window = txt.slice(from, from + hi);
            const punctRe = /[\.\!\?]["')\]]?\s+(?=[A-Z"'(])/g;
            let bestIdx = -1;
            let m;
            while ((m = punctRe.exec(window)) !== null) {
                const idx = m.index + m[0].length;
                if (idx >= lo && idx <= hi) bestIdx = idx;
            }
            if (bestIdx > 0) return bestIdx;
            const ws = txt.indexOf(" ", from + Math.floor(lo * 0.9));
            return ws > 0 ? ws - from : hi;
        };

        // Chunk 0: 80-160 chars.
        const firstCut = cutAt(t, 0, 70, 170);
        out.push(t.slice(0, firstCut).trim());
        i = firstCut;
        // Following chunks: 180-320 chars.
        while (i < t.length) {
            const remaining = t.length - i;
            if (remaining <= 320) {
                out.push(t.slice(i).trim());
                break;
            }
            const nextCut = cutAt(t, i, 150, 320);
            out.push(t.slice(i, i + nextCut).trim());
            i += nextCut;
        }
        return out.filter((c) => c.length > 0);
    }

    function interruptAll() {
        if (audioRef.current) {
            try {
                // The current value can be a real Audio element OR a cancel
                // sentinel from the pipelined playOnyx. Handle both.
                if (typeof audioRef.current.cancel === "function") {
                    audioRef.current.cancel();
                }
                if (typeof audioRef.current.pause === "function") {
                    audioRef.current.pause();
                    audioRef.current.currentTime = 0;
                }
            } catch (_e) {
                // ignore
            }
            audioRef.current = null;
        }
        setAudioPlaying(false);
        if (sendDebounceRef.current) {
            clearTimeout(sendDebounceRef.current);
            sendDebounceRef.current = null;
        }
        if (listening) stopMic();
        if (activeMsgTs) setInterruptedAt(activeMsgTs);
        toast.message("Interrupted.");
    }

    function continueFromInterrupt() {
        if (!lastQuery) return;
        const followUp = `Continue from where the response was interrupted. Original question: ${lastQuery}. Pick up the seven-stage arc where it left off and finish the answer without repeating earlier paragraphs.`;
        setInterruptedAt(null);
        handleSend(followUp);
    }

    function openCall() {
        if (!micSupported) {
            toast.error("Voice input isn't supported in this browser. Try Chrome or Safari.");
            return;
        }
        if (listening) stopMic();
        if (audioRef.current) {
            try {
                audioRef.current.pause();
            } catch (_e) {
                // ignore
            }
        }
        setCallOpen(true);
    }

    return (
        <section
            className={
                focused
                    ? "fixed inset-0 z-[60] flex flex-col p-3 sm:p-5 bg-[#050813]/82 backdrop-blur-md"
                    : "space-y-5 max-w-3xl mx-auto"
            }
            style={
                focused
                    ? {
                          height: vvHeight ? `${vvHeight}px` : "100dvh",
                          overscrollBehavior: "contain",
                          touchAction: "pan-y",
                      }
                    : undefined
            }
            data-testid="chat-surface"
            data-focused={focused ? "true" : "false"}
        >
            {focused && (
                <button
                    type="button"
                    data-testid="chat-fullscreen-close"
                    onClick={() => {
                        if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
                            document.activeElement.blur();
                        }
                        setFocused(false);
                        setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }, 60);
                    }}
                    aria-label="Close fullscreen chat"
                    title="Close fullscreen (Esc)"
                    className="fixed top-3 right-3 sm:top-4 sm:right-4 z-[70] h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-[#0A1220]/85 backdrop-blur-md border border-[#D4AF37]/55 text-[#F9E596] flex items-center justify-center shadow-[0_8px_22px_rgba(0,0,0,0.45)] hover:bg-[#101C30]/95 hover:scale-105 active:scale-95 transition"
                >
                    <X size={22} strokeWidth={2.1} />
                </button>
            )}
            {focused && (
                <div className="max-w-5xl w-full mx-auto mb-3 flex items-center justify-between gap-3 pr-14 sm:pr-16">
                    <button
                        type="button"
                        data-testid="chat-back-btn"
                        onClick={() => {
                            // Blur any focused field so the mobile keyboard
                            // dismisses, then return to the top of landing.
                            if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
                                document.activeElement.blur();
                            }
                            setFocused(false);
                            // Defer so the focused panel's cleanup (which
                            // restores body styles and scrollY) lands first.
                            setTimeout(() => {
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }, 60);
                        }}
                        className="codex-button !py-2.5 !px-4 !text-sm"
                        aria-label="Exit focused chat and return to landing"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Exit
                    </button>
                    <p className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#D4AF37]/80 hidden sm:block">
                        Focused Chat · Esc or tap ✕ to exit
                    </p>
                </div>
            )}
            <div
                className={`codex-card overflow-hidden ${focused ? "max-w-5xl w-full mx-auto flex-1 flex flex-col" : ""}`}
                data-testid="chat-shell"
            >
                {/* Unified glass header: surface toggle on the left, voice + expand on the right */}
                <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-[#D4AF37]/25 bg-[#070D1A]/15">
                    <SurfaceTabs tab={tab} setTab={setTab} />
                    <div className="flex items-center gap-2">
                        {!focused && (
                            <button
                                data-testid="chat-expand-btn"
                                type="button"
                                onClick={() => setFocused(true)}
                                title="Expand chat full screen"
                                className="codex-button !py-1.5 !px-3 text-[10px]"
                                aria-label="Expand chat full screen"
                            >
                                <Maximize2 size={14} strokeWidth={1.6} />
                                <span className="hidden sm:inline">Expand</span>
                            </button>
                        )}
                        <button
                            data-testid="codex-autospeak-toggle"
                            onClick={toggleAutoSpeak}
                            title={autoSpeak ? "Disable voice playback" : "Enable voice playback"}
                            className={`codex-button !py-1.5 !px-3 text-[10px] ${
                                autoSpeak ? "!border-[#D4AF37]/80 !text-[#FFF6D4]" : ""
                            }`}
                        >
                            {autoSpeak ? <Volume2 size={14} strokeWidth={1.6} /> : <VolumeX size={14} strokeWidth={1.6} />}
                            <span className="hidden sm:inline">{autoSpeak ? "Voice On" : "Voice Off"}</span>
                        </button>
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    className={`p-4 sm:p-6 space-y-5 ${
                        focused
                            ? "flex-1 overflow-y-auto scroll-thin"
                            : messages.length === 0 && !loading
                            ? "min-h-[36vh] overflow-visible"
                            : "max-h-[52vh] min-h-[36vh] overflow-y-auto scroll-thin"
                    }`}
                    data-testid="chat-window"
                >
                    {messages.length === 0 && !loading && <ChatEmpty onPick={(q) => handleSend(q)} />}
                    {messages.map((m, idx) => {
                        const isLast = idx === messages.length - 1;
                        return (
                            <Message
                                key={idx}
                                m={m}
                                onDownloadStudy={() => handleDownloadFullStudy(m.query)}
                                onSpeak={() => speakMessage(m)}
                                ttsSupported={ttsSupported}
                                speaking={speaking}
                                showInterrupt={isLast && (audioPlaying || (loading && m.role === "user"))}
                                showContinue={isLast && interruptedAt === m.ts}
                                onInterrupt={interruptAll}
                                onContinue={continueFromInterrupt}
                                onAskFollowUp={(q) => handleSend(q)}
                            />
                        );
                    })}
                    {loading && (
                        <div className="flex items-center gap-2 text-[#D4AF37] font-mono-os text-xs tracking-[0.25em] uppercase">
                            <Loader2 size={14} className="animate-spin" strokeWidth={1.4} />
                            Fanning out across every Codex engine · Synthesizing…
                        </div>
                    )}
                </div>

                {/* Footer: input bar bound to the same glass panel */}
                <div className="px-4 sm:px-5 py-4 border-t border-[#D4AF37]/25 bg-[#070D1A]/15">
                    {messages.some((m) => m.role === "user") && (
                        <div className="flex items-center justify-between gap-2 mb-3">
                            <p className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#94A3B8]">
                                Synthesize a downloadable packet from this exchange
                            </p>
                            <button
                                data-testid="chat-generate-study-btn"
                                onClick={handleGenerateStudyFromConversation}
                                disabled={studyBusy}
                                className="codex-button !px-3 !py-2 !text-[10px] !text-[#F9E596] !border-[#D4AF37]/70 hover:!border-[#F9E596] disabled:opacity-40 disabled:cursor-not-allowed"
                                aria-label="Generate study from this conversation"
                            >
                                {studyBusy ? (
                                    <Loader2 size={12} className="animate-spin" strokeWidth={1.6} />
                                ) : (
                                    <FileDown size={12} strokeWidth={1.6} />
                                )}
                                {studyBusy ? "Composing…" : "Generate Study"}
                            </button>
                        </div>
                    )}
                    <div className="relative">
                        <textarea
                            data-testid="codex-query-input"
                            value={input}
                            onChange={(e) => setInput(e.target.value)}
                            onFocus={() => setFocused(true)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter" && !e.shiftKey) {
                                    e.preventDefault();
                                    handleSend();
                                }
                            }}
                            rows={2}
                            placeholder={
                                listening
                                    ? "Listening…"
                                    : "Ask the Codex…"
                            }
                            className={`codex-input resize-none pr-28 ${listening ? "blue-glow" : ""}`}
                        />
                        <div className="absolute right-3 bottom-3 flex items-center gap-2">
                            <button
                                data-testid="codex-phone-btn"
                                onClick={openCall}
                                title="Open voice call with the Codex OS"
                                className="codex-icon-button"
                                aria-label="Open voice call with the Codex OS"
                            >
                                <Phone size={16} strokeWidth={1.6} />
                            </button>
                            <button
                                data-testid="codex-send-btn"
                                onClick={() => handleSend()}
                                disabled={loading || !input.trim()}
                                className="codex-icon-button codex-icon-button-primary"
                                aria-label="Send"
                            >
                                <Send size={16} strokeWidth={1.6} />
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <CallModal
                open={callOpen}
                onClose={() => setCallOpen(false)}
                sessionId={sessionId}
            />
        </section>
    );
}

function SegmentedTabs() {
    return null; // unused — kept for backward compatibility, real tabs are SurfaceTabs
}

function ModeToggle({ value, onChange }) {
    const opts = [
        { v: "chat", label: "Chat" },
        { v: "restoration", label: "Restoration Matrix" },
    ];
    return (
        <div className="inline-flex border border-[#D4AF37]/30 rounded-md overflow-hidden" data-testid="codex-mode-toggle">
            {opts.map((o) => (
                <button
                    key={o.v}
                    onClick={() => onChange(o.v)}
                    data-testid={`mode-${o.v}`}
                    className={`px-3 py-2 text-[10px] tracking-[0.28em] uppercase font-mono-os transition ${
                        value === o.v
                            ? "bg-[#D4AF37]/15 text-[#F9E596]"
                            : "text-[#94A3B8] hover:text-[#D4AF37]"
                    }`}
                >
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function ChatEmpty({ onPick }) {
    const samples = [
        "How does the Codex read particle physics — can the smallest things be theological?",
        "Is it okay for Christians to use AI? What's the long arc?",
        "Predestination or free will — which is it, and why?",
        "What is the satanic hierarchy and how does a stronghold actually form?",
    ];
    return (
        <div className="flex justify-center py-8" data-testid="codex-empty-state">
            <div className="border border-[#D4AF37]/30 rounded-lg px-6 py-8 max-w-xl w-full text-center">
                <p className="font-serif-display text-3xl text-[#F9E596]">The OS is listening.</p>
                <p className="mt-2 text-[#CBD5E1] text-base leading-relaxed">
                    Ask a question by typing — or tap the{" "}
                    <Phone size={16} strokeWidth={1.8} className="inline align-[-2px] text-[#F9E596]" aria-label="phone icon" />
                    {" "}phone icon to talk to the Codex by voice.
                </p>
                <div className="mt-5 flex flex-wrap gap-2 justify-center">
                    {samples.map((s, i) => (
                        <button
                            key={i}
                            data-testid={`codex-sample-${i}`}
                            onClick={() => onPick(s)}
                            className="codex-chip"
                        >
                            {s}
                        </button>
                    ))}
                </div>
            </div>
        </div>
    );
}

function Message({ m, onDownloadStudy, onSpeak, ttsSupported, speaking, showInterrupt, showContinue, onInterrupt, onContinue, onAskFollowUp }) {
    if (m.role === "user") {
        return (
            <div className="flex justify-end" data-testid="msg-user">
                <div className="max-w-[88%] bg-[#070D1A]/25 border border-[#D4AF37]/35 rounded-lg px-4 py-3 text-base text-[#F1F5F9] leading-relaxed">
                    {m.text}
                </div>
            </div>
        );
    }
    const blocked = m.blocked;
    return (
        <div className="flex" data-testid="msg-os" data-os-ts={m.ts}>
            <div className="max-w-full w-full">
                <div className="flex items-center gap-2 mb-2 font-mono-os text-[10px] tracking-[0.3em] uppercase">
                    <span className={`w-1.5 h-1.5 rounded-full ${blocked ? "bg-[#F43F5E]" : "bg-[#10B981]"}`} />
                    <span className={blocked ? "text-[#F43F5E]" : "text-[#10B981]"}>
                        {blocked ? "OS Status · Blocked" : "OS Response"}
                    </span>
                    <span className="text-[#64748B]">·</span>
                    <span className="text-[#D4AF37]/70">Mode: {m.mode || "chat"}</span>
                </div>
                <div
                    className={`border rounded-lg p-4 sm:p-5 text-[17px] leading-[1.75] whitespace-pre-wrap text-[#F1F5F9] ${
                        blocked
                            ? "border-[#F43F5E]/40 bg-[#1A0F18]/25"
                            : "border-[#D4AF37]/35 bg-[#070D1A]/25"
                    }`}
                >
                    {blocked && (
                        <div className="flex items-center gap-2 text-[#F43F5E] mb-2 text-xs font-mono-os tracking-[0.25em] uppercase">
                            <AlertCircle size={14} strokeWidth={1.4} />
                            Fail-closed
                        </div>
                    )}
                    {m.text}
                </div>
                <SourceTrace layers={m.layers} testid={`sealed-trace-${m.ts}`} />
                {!blocked && m.query && (
                    <CompareWitnesses topic={m.query} />
                )}
                {m.followUps && m.followUps.length > 0 && (
                    <div className="mt-3" data-testid={`followups-${m.ts}`}>
                        <p className="font-mono-os text-[10px] tracking-[0.3em] uppercase text-[#94A3B8] mb-2">
                            Continue the thread
                        </p>
                        <div className="flex flex-wrap gap-2">
                            {m.followUps.map((fu, i) => {
                                const question = typeof fu === "string" ? fu : fu.question;
                                return (
                                    <button
                                        key={i}
                                        type="button"
                                        data-testid={`followup-${m.ts}-${i}`}
                                        onClick={() => onAskFollowUp && onAskFollowUp(question)}
                                        className="text-left px-3 py-2 rounded-lg border border-[#D4AF37]/35 bg-[#070D1A]/20 hover:text-[#F9E596] hover:border-[#D4AF37]/70 hover:bg-[#070D1A]/40 transition max-w-full"
                                    >
                                        <div className="flex items-start gap-2">
                                            <span className="text-[#F9E596] mt-[2px] flex-shrink-0">→</span>
                                            <span className="text-[14.5px] leading-snug text-[#E2E8F0]">
                                                {question}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}
                {showInterrupt && (
                    <div className="mt-3">
                        <button
                            data-testid="codex-interrupt-btn"
                            onClick={onInterrupt}
                            className="codex-button text-[10px] !border-[#F43F5E]/60 !text-[#F87171]"
                        >
                            <Square size={12} strokeWidth={2} fill="currentColor" />
                            Interrupt
                        </button>
                    </div>
                )}
                {showContinue && (
                    <div className="mt-3">
                        <button
                            data-testid="codex-continue-btn"
                            onClick={onContinue}
                            className="codex-button codex-button-primary text-[10px]"
                        >
                            <Send size={12} strokeWidth={1.6} />
                            Continue
                        </button>
                    </div>
                )}
                {!blocked && (
                    <div className="flex gap-2 mt-4 flex-wrap">
                        {ttsSupported && (
                            <button
                                data-testid="msg-speak-btn"
                                onClick={onSpeak}
                                className={`codex-button text-[10px] ${
                                    speaking ? "!border-[#4A90E2]/70 !text-[#4A90E2]" : ""
                                }`}
                            >
                                {speaking ? <VolumeX size={14} strokeWidth={1.4} /> : <Volume2 size={14} strokeWidth={1.4} />}
                                {speaking ? "Stop" : "Read Aloud"}
                            </button>
                        )}
                        {m.query && (
                            <button
                                data-testid="msg-create-study-btn"
                                onClick={onDownloadStudy}
                                className="codex-button text-[10px]"
                            >
                                <FileDown size={14} strokeWidth={1.4} />
                                Create a Study About This
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

// --------------------------------------------------------------------------
// Study surface
// --------------------------------------------------------------------------

function StudySurface({ tab, setTab }) {
    const [topic, setTopic] = useState("");
    const [packet, setPacket] = useState(null);
    const [busy, setBusy] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);
    // Focused / full-screen mode — same UX as the chat surface (Expand
    // button to enter, Back / ESC to exit, body scroll locked, dynamic
    // viewport height so the mobile keyboard does not cover the input).
    const [focused, setFocused] = useState(false);
    const [vvHeight, setVvHeight] = useState(
        typeof window !== "undefined"
            ? (window.visualViewport && window.visualViewport.height) || window.innerHeight
            : 0
    );

    useEffect(() => {
        if (!focused) return;
        const scrollY = window.scrollY;
        const body = document.body;
        const prev = {
            overflow: body.style.overflow,
            position: body.style.position,
            top: body.style.top,
            width: body.style.width,
            overscroll: document.documentElement.style.overscrollBehavior,
        };
        body.style.overflow = "hidden";
        body.style.position = "fixed";
        body.style.top = `-${scrollY}px`;
        body.style.width = "100%";
        document.documentElement.style.overscrollBehavior = "contain";

        const vv = window.visualViewport;
        const sync = () => setVvHeight(vv ? vv.height : window.innerHeight);
        sync();
        if (vv) {
            vv.addEventListener("resize", sync);
            vv.addEventListener("scroll", sync);
        } else {
            window.addEventListener("resize", sync);
        }
        const onKey = (e) => {
            if (e.key === "Escape") setFocused(false);
        };
        window.addEventListener("keydown", onKey);
        return () => {
            body.style.overflow = prev.overflow;
            body.style.position = prev.position;
            body.style.top = prev.top;
            body.style.width = prev.width;
            document.documentElement.style.overscrollBehavior = prev.overscroll;
            window.scrollTo(0, scrollY);
            window.removeEventListener("keydown", onKey);
            if (vv) {
                vv.removeEventListener("resize", sync);
                vv.removeEventListener("scroll", sync);
            } else {
                window.removeEventListener("resize", sync);
            }
        };
    }, [focused]);

    async function handleGenerate() {
        const value = topic.trim();
        if (!value) {
            toast.error("Enter a topic to generate a study.");
            return;
        }
        setBusy(true);
        setPacket(null);
        const tId = toast.loading("Routing to the Codex OS…");
        try {
            const result = await codexApi.study(value);
            setPacket(result);
            toast.success("Study packet ready. Download the PDF to keep it.", { id: tId });
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not generate study.", { id: tId });
        } finally {
            setBusy(false);
        }
    }

    async function handlePdf() {
        if (!packet) return;
        setPdfBusy(true);
        const tId = toast.loading("Rendering PDF…");
        try {
            const blob = await codexApi.studyPdf(packet);
            const safe = (packet.title || "codex_study")
                .replace(/[^a-z0-9_-]+/gi, "_")
                .toLowerCase();
            downloadBlob(blob, `${safe}.pdf`);
            toast.success("PDF downloaded.", { id: tId });
        } catch {
            toast.error("Could not render PDF.", { id: tId });
        } finally {
            setPdfBusy(false);
        }
    }

    return (
        <section
            className={
                focused
                    ? "fixed inset-0 z-[60] flex flex-col p-3 sm:p-5 bg-[#050813]/82 backdrop-blur-md"
                    : "space-y-6 max-w-3xl mx-auto"
            }
            style={
                focused
                    ? {
                          height: vvHeight ? `${vvHeight}px` : "100dvh",
                          overscrollBehavior: "contain",
                          touchAction: "pan-y",
                      }
                    : undefined
            }
            data-testid="study-surface"
            data-focused={focused ? "true" : "false"}
        >
            {focused && (
                <button
                    type="button"
                    data-testid="study-fullscreen-close"
                    onClick={() => {
                        if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
                            document.activeElement.blur();
                        }
                        setFocused(false);
                        setTimeout(() => {
                            window.scrollTo({ top: 0, behavior: "smooth" });
                        }, 60);
                    }}
                    aria-label="Close fullscreen study"
                    title="Close fullscreen (Esc)"
                    className="fixed top-3 right-3 sm:top-4 sm:right-4 z-[70] h-11 w-11 sm:h-12 sm:w-12 rounded-full bg-[#0A1220]/85 backdrop-blur-md border border-[#D4AF37]/55 text-[#F9E596] flex items-center justify-center shadow-[0_8px_22px_rgba(0,0,0,0.45)] hover:bg-[#101C30]/95 hover:scale-105 active:scale-95 transition"
                >
                    <X size={22} strokeWidth={2.1} />
                </button>
            )}
            {focused && (
                <div className="max-w-5xl w-full mx-auto mb-3 flex items-center justify-between gap-3 pr-14 sm:pr-16">
                    <button
                        type="button"
                        data-testid="study-back-btn"
                        onClick={() => {
                            if (typeof document !== "undefined" && document.activeElement && document.activeElement.blur) {
                                document.activeElement.blur();
                            }
                            setFocused(false);
                            setTimeout(() => {
                                window.scrollTo({ top: 0, behavior: "smooth" });
                            }, 60);
                        }}
                        className="codex-button !py-2.5 !px-4 !text-sm"
                        aria-label="Exit focused study and return to landing"
                    >
                        <ArrowLeft size={16} strokeWidth={2} />
                        Exit
                    </button>
                    <p className="font-mono-os text-xs tracking-[0.28em] uppercase text-[#D4AF37]/80 hidden sm:block">
                        Focused Study · Esc or tap ✕ to exit
                    </p>
                </div>
            )}
            <div
                className={`codex-card overflow-hidden ${
                    focused ? "max-w-5xl w-full mx-auto flex-1 flex flex-col" : ""
                }`}
            >
                <div className="flex items-center justify-between gap-3 px-4 sm:px-5 py-3 border-b border-[#D4AF37]/25 bg-[#070D1A]/15">
                    <SurfaceTabs tab={tab} setTab={setTab} />
                    {!focused && (
                        <button
                            data-testid="study-expand-btn"
                            type="button"
                            onClick={() => setFocused(true)}
                            title="Expand study full screen"
                            className="codex-button !py-1.5 !px-3 text-[10px]"
                            aria-label="Expand study full screen"
                        >
                            <Maximize2 size={14} strokeWidth={1.6} />
                            <span className="hidden sm:inline">Expand</span>
                        </button>
                    )}
                </div>
                <div
                    className={`p-5 sm:p-6 space-y-4 ${focused ? "flex-1 overflow-y-auto scroll-thin" : ""}`}
                >
                    <p className="text-[#94A3B8] text-sm leading-relaxed">
                        Enter a topic to compose a downloadable theological study packet, read
                        through the Codex's interpretive lens. Packets are export-only — the PDF is the kept copy.
                    </p>
                    <label className="block font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/80">
                        Study Topic
                    </label>
                    <textarea
                        data-testid="generate-topic-input"
                        rows={2}
                        value={topic}
                        onChange={(e) => setTopic(e.target.value)}
                        onFocus={() => setFocused(true)}
                        placeholder="e.g. The Bride being prepared for the Bridegroom"
                        className="codex-input"
                    />
                    <div className="flex justify-end gap-2">
                        <button
                            data-testid="generate-btn"
                            onClick={handleGenerate}
                            disabled={busy || !topic.trim()}
                            className="codex-button codex-button-primary"
                        >
                            {busy ? <Loader2 size={14} className="animate-spin" /> : <FilePlus2 size={14} strokeWidth={1.6} />}
                            Generate Study Packet
                        </button>
                    </div>

                    {!packet && !busy && (
                        <div
                            className="text-center py-10 px-6 text-[#CBD5E1] text-sm border border-[#D4AF37]/30 rounded-lg"
                            data-testid="generate-empty"
                        >
                            <BookOpen size={28} strokeWidth={1.2} className="mx-auto text-[#D4AF37]/70" />
                            <p className="mt-3 font-serif-display text-xl text-[#F9E596]">No study packet yet.</p>
                            <p className="mt-1 text-[#CBD5E1]">Enter a topic above. The PDF is the only kept copy.</p>
                        </div>
                    )}

                    {packet && (
                        <article
                            className="p-5 sm:p-6 space-y-5 border border-[#D4AF37]/30 rounded-lg"
                            data-testid="generate-packet"
                        >
                            <header className="flex flex-wrap items-baseline justify-between gap-3">
                                <div className="flex-1 min-w-0">
                                    <h2 className="font-serif-display text-2xl sm:text-3xl text-[#F9E596] leading-tight">
                                        {packet.title}
                                    </h2>
                                    <p className="font-mono-os text-[10px] tracking-[0.28em] uppercase text-[#D4AF37]/60 mt-2">
                                        {packet.layers_consulted || 0} sealed Codex layers · export-only
                                    </p>
                                </div>
                                <button
                                    data-testid="generate-pdf-btn"
                                    onClick={handlePdf}
                                    disabled={pdfBusy}
                                    className="codex-button codex-button-primary"
                                >
                                    {pdfBusy ? <Loader2 size={14} className="animate-spin" /> : <FileDown size={14} strokeWidth={1.4} />}
                                    Download PDF
                                </button>
                            </header>

                            <Block label="Summary" value={packet.summary} />
                            <Block label="Synthesis" value={packet.synthesis} />
                            {packet.scriptural_references?.length > 0 && (
                                <Block
                                    label="Scriptural Pattern References"
                                    value={packet.scriptural_references.map((r) => `• ${r}`).join("\n")}
                                />
                            )}
                            <Block label="Codex Routing Summary" value={packet.codex_routing_summary} />
                            <Block label="Scope Notes" value={packet.scope_notes} />
                            <SourceTrace layers={packet.layers_consulted} testid="generate-sealed-trace" />
                        </article>
                    )}
                </div>
            </div>
        </section>
    );
}

function Block({ label, value }) {
    if (!value) return null;
    return (
        <div>
            <div className="font-mono-os text-[10px] tracking-[0.3em] uppercase text-[#D4AF37]/80 mb-2">
                {label}
            </div>
            <div className="text-[#E2E8F0] text-[14.5px] leading-[1.7] whitespace-pre-wrap">
                {value}
            </div>
        </div>
    );
}
